import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import "./bookLessonReaderPage.css";
import { ROUTES, sidebarNavItems } from "../../../constant";
import { clearData } from "../../../utils/localStorage";
import {
    bookLessonSlidesService,
    bookPurchasedDetailService,
    bookSlideProgressUpdateService,
    createBookBookmarkService,
    createBookNoteService,
    deleteBookBookmarkService,
    deleteBookNoteService,
    updateBookNoteService,
} from "../../../modules/services";
import SideBar from "../../commanComponents/sidebar/sidebar";
import TopNavigation from "../../commanComponents/topNavigation/topNavigation";

const responseOk = (response) => response?.status_code === 200 || response?.status_code === 201;

const getSlideStatus = (slide) => slide?.status || "not_started";
const getSlideNotes = (slide) => Array.isArray(slide?.notes) ? slide.notes : [];
const NOTE_ANCHOR_SELECTOR = [
    ".frl-slide__header",
    ".frl-slide__content",
    ".frl-slide__media",
    ".slide-line",
    ".slide-media",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "p",
    "li",
    "blockquote",
    "figure",
    "figcaption",
    "img",
    "video",
    "iframe",
    "table",
    "tr",
    "td",
    "th",
    "pre",
].join(", ");

const clampPercent = (value, minimum = 0, maximum = 100) => (
    Math.min(maximum, Math.max(minimum, Number(value || 0)))
);

const toNumberOrNull = (value) => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const normalizeNote = (note, fallbackSlideId, fallbackSlideOrder) => ({
    ...note,
    slide: Number(note.slide || fallbackSlideId),
    slide_order: Number(note.slide_order || fallbackSlideOrder),
    anchor_selector: note.anchor_selector || "",
    anchor_x_percent: toNumberOrNull(note.anchor_x_percent),
    anchor_y_percent: toNumberOrNull(note.anchor_y_percent),
    content_x_percent: toNumberOrNull(note.content_x_percent ?? note.x_percent),
    content_y_percent: toNumberOrNull(note.content_y_percent ?? note.y_percent),
    x_percent: Number(note.x_percent ?? note.content_x_percent ?? 0),
    y_percent: Number(note.y_percent ?? note.content_y_percent ?? 0),
});

const getNotePositionKey = (note) => note?.id ? `saved-${note.id}` : "draft-note";

const BookLessonReaderPage = () => {
    const navigate = useNavigate();
    const { slug, lessonId } = useParams();
    const [searchParams] = useSearchParams();
    const startedAtRef = useRef(Date.now());
    const currentSlideIdRef = useRef(null);
    const htmlContentRef = useRef(null);
    const contentStageRef = useRef(null);
    const contentMarkupRef = useRef(null);
    const notePositionFrameRef = useRef(null);

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [pageError, setPageError] = useState("");
    const [bookData, setBookData] = useState(null);
    const [lessonData, setLessonData] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isReadMode, setIsReadMode] = useState(false);
    const [isBookmarkSaving, setIsBookmarkSaving] = useState(false);
    const [isNoteMode, setIsNoteMode] = useState(false);
    const [isNoteSaving, setIsNoteSaving] = useState(false);
    const [activeNote, setActiveNote] = useState(null);
    const [notePositions, setNotePositions] = useState({});

    const book = bookData?.book;
    const requestedSlideId = Number(searchParams.get("slide_id") || 0);
    const slides = useMemo(() => {
        return (Array.isArray(lessonData?.slides) ? lessonData.slides : [])
            .map((slide, index) => ({
                ...slide,
                order: Number(slide.order || index + 1),
                estimated_time_seconds: Number(slide.estimated_time_seconds || 0),
                notes: getSlideNotes(slide).map((note) => normalizeNote(
                    note,
                    slide.id,
                    slide.order || index + 1
                )),
            }))
            .sort((first, second) => first.order - second.order);
    }, [lessonData?.slides]);

    const currentSlide = slides[currentIndex];
    const completedScreens = slides.filter((slide) => getSlideStatus(slide) === "completed").length;
    const lessonProgress = slides.length ? Math.round((completedScreens / slides.length) * 100) : 0;
    const firstUnreadIndex = slides.findIndex((slide) => getSlideStatus(slide) !== "completed");
    const maxAccessibleIndex = firstUnreadIndex >= 0
        ? Math.max(firstUnreadIndex, currentIndex)
        : Math.max(slides.length - 1, 0);

    const updateLocalSlide = useCallback((slideId, patch) => {
        setLessonData((previous) => {
            if (!previous || !Array.isArray(previous.slides)) return previous;

            return {
                ...previous,
                slides: previous.slides.map((slide) => (
                    slide.id === slideId
                        ? { ...slide, ...patch }
                        : slide
                )),
            };
        });
    }, []);

    const updateSlideNotes = useCallback((slideId, updater) => {
        setLessonData((previous) => {
            if (!previous || !Array.isArray(previous.slides)) return previous;

            return {
                ...previous,
                slides: previous.slides.map((slide) => {
                    if (slide.id !== slideId) return slide;

                    const nextNotes = typeof updater === "function"
                        ? updater(Array.isArray(slide.notes) ? slide.notes : [])
                        : updater;

                    return {
                        ...slide,
                        notes: Array.isArray(nextNotes) ? nextNotes : [],
                    };
                }),
            };
        });
    }, []);

    const currentSlideNotes = useMemo(() => (
        getSlideNotes(currentSlide).map((note) => normalizeNote(
            note,
            currentSlide?.id,
            currentSlide?.order
        ))
    ), [currentSlide]);

    const tagNoteAnchors = useCallback(() => {
        const markup = contentMarkupRef.current;
        if (!markup) return;

        const anchors = Array.from(markup.querySelectorAll(NOTE_ANCHOR_SELECTOR))
            .filter((element) => element.getBoundingClientRect().width > 0 || element.getBoundingClientRect().height > 0);

        anchors.forEach((element, index) => {
            element.setAttribute("data-note-anchor-key", `note-anchor-${index + 1}`);
        });
    }, []);

    const resolveNotePosition = useCallback((note) => {
        const stage = contentStageRef.current;
        if (!stage || !note) return null;

        const stageRect = stage.getBoundingClientRect();
        if (!stageRect.width || !stageRect.height) return null;

        let leftPx = null;
        let topPx = null;

        if (note.anchor_selector) {
            const anchorElement = stage.querySelector(`[data-note-anchor-key="${note.anchor_selector}"]`);
            const anchorRect = anchorElement?.getBoundingClientRect();

            if (anchorRect && anchorRect.width && anchorRect.height) {
                leftPx = (anchorRect.left - stageRect.left) + ((clampPercent(note.anchor_x_percent) / 100) * anchorRect.width);
                topPx = (anchorRect.top - stageRect.top) + ((clampPercent(note.anchor_y_percent) / 100) * anchorRect.height);
            }
        }

        if (leftPx === null || topPx === null) {
            const fallbackX = clampPercent(note.content_x_percent ?? note.x_percent);
            const fallbackY = clampPercent(note.content_y_percent ?? note.y_percent);
            leftPx = (fallbackX / 100) * stageRect.width;
            topPx = (fallbackY / 100) * stageRect.height;
        }

        leftPx = Math.min(stageRect.width - 12, Math.max(12, leftPx));
        topPx = Math.min(stageRect.height - 12, Math.max(12, topPx));

        return {
            leftPx,
            topPx,
            leftPercent: stageRect.width ? (leftPx / stageRect.width) * 100 : 0,
            topPercent: stageRect.height ? (topPx / stageRect.height) * 100 : 0,
        };
    }, []);

    const recalculateNotePositions = useCallback(() => {
        const nextPositions = {};
        currentSlideNotes.forEach((note) => {
            const position = resolveNotePosition(note);
            if (position) {
                nextPositions[getNotePositionKey(note)] = position;
            }
        });

        if (activeNote && currentSlide?.id === activeNote.slide) {
            const position = resolveNotePosition(activeNote);
            if (position) {
                nextPositions[getNotePositionKey(activeNote)] = position;
            }
        }

        setNotePositions(nextPositions);
    }, [activeNote, currentSlide?.id, currentSlideNotes, resolveNotePosition]);

    const scheduleNotePositionUpdate = useCallback(() => {
        if (notePositionFrameRef.current) {
            window.cancelAnimationFrame(notePositionFrameRef.current);
        }

        notePositionFrameRef.current = window.requestAnimationFrame(() => {
            recalculateNotePositions();
        });
    }, [recalculateNotePositions]);

    const getTimeDelta = useCallback(() => {
        const now = Date.now();
        const seconds = Math.max(1, Math.round((now - startedAtRef.current) / 1000));
        startedAtRef.current = now;
        return seconds;
    }, []);

    const saveSlideProgress = useCallback(async (slide, options = {}) => {
        if (!book?.id || !lessonId || !slide?.id) return null;

        const shouldShowSaving = Boolean(options.showSaving);
        if (shouldShowSaving) setIsSaving(true);

        const payload = {
            time_delta: getTimeDelta(),
            mark_started: options.markStarted ?? true,
            mark_completed: Boolean(options.markCompleted),
        };

        const response = await bookSlideProgressUpdateService(book.id, lessonId, slide.id, payload, {});

        if (shouldShowSaving) setIsSaving(false);

        if (response === "error") {
            clearData();
            navigate(ROUTES.LOGIN);
            return null;
        }

        if (responseOk(response)) {
            updateLocalSlide(slide.id, {
                status: response.data?.status || (payload.mark_completed ? "completed" : "in_progress"),
                time_spent_seconds: response.data?.time_spent_seconds ?? slide.time_spent_seconds,
                completed_at: response.data?.completed_at ?? slide.completed_at,
                access_count: response.data?.access_count ?? slide.access_count,
            });
        }

        return response;
    }, [book?.id, getTimeDelta, lessonId, navigate, updateLocalSlide]);

    const loadReader = useCallback(async () => {
        setIsLoading(true);
        setPageError("");

        try {
            const detailResponse = await bookPurchasedDetailService(slug, {});

            if (detailResponse === "error") {
                clearData();
                navigate(ROUTES.LOGIN);
                return;
            }

            if (!responseOk(detailResponse)) {
                throw new Error(detailResponse?.message?.[0] || "Unable to load this book.");
            }

            const detailData = detailResponse.data;
            const bookId = detailData?.book?.id;

            if (!bookId) {
                throw new Error("Book details are incomplete.");
            }

            const lessonResponse = await bookLessonSlidesService(bookId, lessonId, {});

            if (lessonResponse === "error") {
                clearData();
                navigate(ROUTES.LOGIN);
                return;
            }

            if (!responseOk(lessonResponse)) {
                throw new Error(lessonResponse?.message?.[0] || "Unable to load this lesson.");
            }

            setBookData(detailData);
            setLessonData(lessonResponse.data);

            const orderedSlides = (lessonResponse.data?.slides || [])
                .map((slide, index) => ({
                    ...slide,
                    order: Number(slide.order || index + 1),
                }))
                .sort((first, second) => first.order - second.order);

            const requestedIndex = requestedSlideId
                ? orderedSlides.findIndex((slide) => slide.id === requestedSlideId)
                : -1;
            const firstAvailableIndex = orderedSlides.findIndex((slide) => slide.status !== "completed");

            setCurrentIndex(requestedIndex >= 0 ? requestedIndex : (firstAvailableIndex >= 0 ? firstAvailableIndex : 0));
            startedAtRef.current = Date.now();
        } catch (error) {
            console.error("Error loading lesson reader:", error);
            setPageError(error?.message || "Unable to load this lesson.");
        } finally {
            setIsLoading(false);
        }
    }, [lessonId, navigate, requestedSlideId, slug]);

    useEffect(() => {
        loadReader();
    }, [loadReader]);

    useEffect(() => {
        if (!currentSlide || currentSlideIdRef.current === currentSlide.id) return;

        currentSlideIdRef.current = currentSlide.id;
        startedAtRef.current = Date.now();

        if (getSlideStatus(currentSlide) === "not_started") {
            saveSlideProgress(currentSlide, { markStarted: true });
        }
    }, [currentSlide, saveSlideProgress]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape") {
                setIsReadMode(false);
                setIsNoteMode(false);
                setActiveNote(null);
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    useEffect(() => {
        setIsNoteMode(false);
        setActiveNote(null);
    }, [currentSlide?.id]);

    useEffect(() => {
        tagNoteAnchors();
        scheduleNotePositionUpdate();
    }, [currentSlide?.content, currentSlide?.id, isReadMode, scheduleNotePositionUpdate, tagNoteAnchors]);

    useEffect(() => {
        scheduleNotePositionUpdate();
    }, [currentSlideNotes, activeNote, scheduleNotePositionUpdate]);

    useEffect(() => {
        const markup = contentMarkupRef.current;
        const stage = contentStageRef.current;
        const content = htmlContentRef.current;
        if (!markup || !stage || !content) return undefined;

        const schedule = () => scheduleNotePositionUpdate();
        const resizeObserver = new ResizeObserver(schedule);
        resizeObserver.observe(markup);
        resizeObserver.observe(stage);
        resizeObserver.observe(content);

        const mediaElements = Array.from(markup.querySelectorAll("img, video, iframe"));
        mediaElements.forEach((element) => {
            element.addEventListener("load", schedule);
            element.addEventListener("loadedmetadata", schedule);
        });

        window.addEventListener("resize", schedule);

        return () => {
            resizeObserver.disconnect();
            mediaElements.forEach((element) => {
                element.removeEventListener("load", schedule);
                element.removeEventListener("loadedmetadata", schedule);
            });
            window.removeEventListener("resize", schedule);
            if (notePositionFrameRef.current) {
                window.cancelAnimationFrame(notePositionFrameRef.current);
                notePositionFrameRef.current = null;
            }
        };
    }, [currentSlide?.id, isReadMode, scheduleNotePositionUpdate]);

    const goBack = () => {
        navigate(`${ROUTES.BOOKS_SELLING_BOOK_DASHBOARD_DETAILS}/${slug || ""}`);
    };

    const goToIndex = async (nextIndex) => {
        if (
            nextIndex < 0 ||
            nextIndex >= slides.length ||
            nextIndex === currentIndex ||
            nextIndex > maxAccessibleIndex
        ) return;

        if (currentSlide) {
            await saveSlideProgress(currentSlide, {
                markCompleted: nextIndex > currentIndex,
                showSaving: true,
            });
        }

        setCurrentIndex(nextIndex);
    };

    const goPrevious = () => goToIndex(currentIndex - 1);

    const goNext = async () => {
        if (!currentSlide) return;

        await saveSlideProgress(currentSlide, { markCompleted: true, showSaving: true });

        if (currentIndex < slides.length - 1) {
            setCurrentIndex((previous) => previous + 1);
            return;
        }

        goBack();
    };

    const markCurrentComplete = async () => {
        if (!currentSlide) return;
        await saveSlideProgress(currentSlide, { markCompleted: true, showSaving: true });
    };

    const toggleBookmark = async () => {
        if (!book?.id || !lessonId || !currentSlide?.id) return;

        setIsBookmarkSaving(true);

        const response = currentSlide.is_bookmarked
            ? await deleteBookBookmarkService(currentSlide.bookmark_id, {})
            : await createBookBookmarkService(book.id, lessonId, { slide_id: currentSlide.id }, {});

        setIsBookmarkSaving(false);

        if (response === "error") {
            clearData();
            navigate(ROUTES.LOGIN);
            return;
        }

        if (!(responseOk(response))) {
            toast.error(response?.message?.[0] || "Unable to update bookmark right now.");
            return;
        }

        if (currentSlide.is_bookmarked) {
            updateLocalSlide(currentSlide.id, {
                is_bookmarked: false,
                bookmark_id: null,
            });
            toast.success("Bookmark removed.");
            return;
        }

        updateLocalSlide(currentSlide.id, {
            is_bookmarked: true,
            bookmark_id: response.data?.id || null,
        });
        toast.success("Page bookmarked.");
    };

    const startNoteMode = () => {
        if (isNoteSaving) return;
        setActiveNote(null);
        setIsNoteMode((previous) => !previous);
    };

    const closeActiveNote = () => {
        setActiveNote(null);
        setIsNoteMode(false);
    };

    const openExistingNote = (note) => {
        setIsNoteMode(false);
        setActiveNote({
            ...normalizeNote(note, currentSlide?.id, currentSlide?.order),
            content: note.content || "",
            isDraft: false,
        });
    };

    const createDraftNote = useCallback((event) => {
        if (!isNoteMode || !currentSlide?.id || isNoteSaving || !contentStageRef.current || !contentMarkupRef.current) return;

        const trigger = event.target.closest(".book-lr-note-chip, .book-lr-note-cloud, .book-lr-note-toggle");
        if (trigger) return;

        const stageBounds = contentStageRef.current.getBoundingClientRect();
        if (!stageBounds.width || !stageBounds.height) return;

        const anchorElements = Array.from(contentMarkupRef.current.querySelectorAll("[data-note-anchor-key]"));
        const directAnchor = event.target.closest?.("[data-note-anchor-key]");
        const anchorElement = directAnchor || anchorElements.reduce((closest, element) => {
            const rect = element.getBoundingClientRect();
            if (!rect.width && !rect.height) return closest;

            const containsPoint = (
                event.clientX >= rect.left &&
                event.clientX <= rect.right &&
                event.clientY >= rect.top &&
                event.clientY <= rect.bottom
            );

            const dx = event.clientX < rect.left
                ? rect.left - event.clientX
                : event.clientX > rect.right
                    ? event.clientX - rect.right
                    : 0;
            const dy = event.clientY < rect.top
                ? rect.top - event.clientY
                : event.clientY > rect.bottom
                    ? event.clientY - rect.bottom
                    : 0;
            const score = containsPoint ? 0 : Math.hypot(dx, dy);

            if (!closest || score < closest.score) {
                return { element, score };
            }

            return closest;
        }, null)?.element;

        const contentX = clampPercent(((event.clientX - stageBounds.left) / stageBounds.width) * 100, 1, 99);
        const contentY = clampPercent(((event.clientY - stageBounds.top) / stageBounds.height) * 100, 1, 99);

        let anchorSelector = "";
        let anchorX = null;
        let anchorY = null;

        if (anchorElement) {
            const anchorBounds = anchorElement.getBoundingClientRect();
            if (anchorBounds.width && anchorBounds.height) {
                anchorSelector = anchorElement.getAttribute("data-note-anchor-key") || "";
                anchorX = clampPercent(((event.clientX - anchorBounds.left) / anchorBounds.width) * 100, 1, 99);
                anchorY = clampPercent(((event.clientY - anchorBounds.top) / anchorBounds.height) * 100, 1, 99);
            }
        }

        setActiveNote({
            id: null,
            slide: currentSlide.id,
            slide_order: currentSlide.order,
            content: "",
            anchor_selector: anchorSelector,
            anchor_x_percent: anchorX !== null ? Number(anchorX.toFixed(2)) : null,
            anchor_y_percent: anchorY !== null ? Number(anchorY.toFixed(2)) : null,
            content_x_percent: Number(contentX.toFixed(2)),
            content_y_percent: Number(contentY.toFixed(2)),
            x_percent: Number(contentX.toFixed(2)),
            y_percent: Number(contentY.toFixed(2)),
            isDraft: true,
        });
        setIsNoteMode(false);
    }, [currentSlide?.id, currentSlide?.order, isNoteMode, isNoteSaving]);

    const handleNoteContentChange = (event) => {
        const value = event.target.value;
        setActiveNote((previous) => previous ? { ...previous, content: value } : previous);
    };

    const saveActiveNote = async () => {
        if (!book?.id || !currentSlide?.id || !activeNote) return;

        const content = (activeNote.content || "").trim();
        if (!content) {
            toast.error("Write a note before saving.");
            return;
        }

        setIsNoteSaving(true);
        const payload = {
            slide_id: currentSlide.id,
            content,
            anchor_selector: activeNote.anchor_selector || "",
            anchor_x_percent: activeNote.anchor_x_percent,
            anchor_y_percent: activeNote.anchor_y_percent,
            content_x_percent: Number(activeNote.content_x_percent ?? activeNote.x_percent),
            content_y_percent: Number(activeNote.content_y_percent ?? activeNote.y_percent),
            x_percent: Number(activeNote.content_x_percent ?? activeNote.x_percent),
            y_percent: Number(activeNote.content_y_percent ?? activeNote.y_percent),
        };

        const response = activeNote.id
            ? await updateBookNoteService(activeNote.id, payload, {})
            : await createBookNoteService(book.id, payload, {});

        setIsNoteSaving(false);

        if (response === "error") {
            clearData();
            navigate(ROUTES.LOGIN);
            return;
        }

        if (!responseOk(response)) {
            toast.error(response?.message?.[0] || "Unable to save note right now.");
            return;
        }

        const savedNote = {
            ...normalizeNote(response.data || {}, currentSlide.id, currentSlide.order),
        };

        updateSlideNotes(currentSlide.id, (notes) => {
            const filtered = notes.filter((note) => note.id !== savedNote.id);
            return [...filtered, savedNote].sort((first, second) => first.id - second.id);
        });

        setActiveNote(null);
        toast.success(activeNote.id ? "Note updated." : "Note saved.");
    };

    const deleteActiveNote = async () => {
        if (!activeNote?.id) {
            setActiveNote(null);
            return;
        }

        setIsNoteSaving(true);
        const response = await deleteBookNoteService(activeNote.id, {});
        setIsNoteSaving(false);

        if (response === "error") {
            clearData();
            navigate(ROUTES.LOGIN);
            return;
        }

        if (!responseOk(response)) {
            toast.error(response?.message?.[0] || "Unable to delete note right now.");
            return;
        }

        updateSlideNotes(currentSlide.id, (notes) => notes.filter((note) => note.id !== activeNote.id));
        setActiveNote(null);
        toast.success("Note deleted.");
    };

    return (
        <div className={`academic-courses-container ${isReadMode ? "book-lr-app-read-mode" : ""}`} style={{ display: "flex" }}>
            {!isReadMode && <SideBar activeItem={sidebarNavItems.bookDashboard} />}
            <div className={`courses-side-container ${isReadMode ? "book-lr-read-shell" : ""}`}>
                {!isReadMode && <TopNavigation activeItem={sidebarNavItems.bookDashboard} />}

                <main className={`book-lr-page ${isReadMode ? "read-mode" : ""}`}>
                    {!isReadMode ? (
                        <header className="book-lr-topbar">
                            <button
                                type="button"
                                className="book-lr-back-btn"
                                onClick={goBack}
                            >
                                <i className="bi bi-arrow-left"></i>
                                Back
                            </button>

                            <div className="book-lr-title-block">
                                <span>{book?.title || "My Book"}</span>
                                <strong>{lessonData?.title || "Lesson reader"}</strong>
                            </div>

                            <div className="book-lr-top-actions">
                                <div className="book-lr-top-status">
                                    <span>{isSaving ? "Saving" : "Progress"}</span>
                                    <strong>{lessonProgress}%</strong>
                                </div>
                                <button
                                    type="button"
                                    className="book-lr-focus-toggle"
                                    disabled={isBookmarkSaving}
                                    onClick={() => setIsReadMode(true)}
                                >
                                    <i className="bi bi-fullscreen"></i>
                                    Read Mode
                                </button>
                                <button
                                    type="button"
                                    className={`book-lr-bookmark-btn ${currentSlide?.is_bookmarked ? "is-active" : ""}`}
                                    disabled={isBookmarkSaving || !currentSlide}
                                    onClick={toggleBookmark}
                                    title={currentSlide?.is_bookmarked ? "Remove bookmark" : "Bookmark this page"}
                                >
                                    <i className={`bi ${currentSlide?.is_bookmarked ? "bi-bookmark-fill" : "bi-bookmark"}`}></i>
                                    {isBookmarkSaving ? "Saving..." : currentSlide?.is_bookmarked ? "Bookmarked" : "Bookmark"}
                                </button>
                                <button
                                    type="button"
                                    className={`book-lr-note-toggle ${isNoteMode ? "is-active" : ""}`}
                                    disabled={isNoteSaving || !currentSlide}
                                    onClick={startNoteMode}
                                >
                                    <i className="bi bi-stickies"></i>
                                    {isNoteMode ? "Tap a spot" : "Add Note"}
                                </button>
                            </div>
                        </header>
                    ) : (
                        <div className="book-lr-read-mode-toolbar">
                            <button
                                type="button"
                                className={`book-lr-read-mode-note ${isNoteMode ? "is-active" : ""}`}
                                onClick={startNoteMode}
                                disabled={isNoteSaving || !currentSlide}
                                aria-label={isNoteMode ? "Choose a spot for your note" : "Add note"}
                                title={isNoteMode ? "Choose a spot for your note" : "Add note"}
                            >
                                <i className="bi bi-stickies"></i>
                            </button>
                            <button
                                type="button"
                                className={`book-lr-read-mode-bookmark ${currentSlide?.is_bookmarked ? "is-active" : ""}`}
                                onClick={toggleBookmark}
                                disabled={isBookmarkSaving || !currentSlide}
                                aria-label={currentSlide?.is_bookmarked ? "Remove bookmark" : "Bookmark this page"}
                                title={currentSlide?.is_bookmarked ? "Remove bookmark" : "Bookmark this page"}
                            >
                                <i className={`bi ${currentSlide?.is_bookmarked ? "bi-bookmark-fill" : "bi-bookmark"}`}></i>
                            </button>
                            <button
                                type="button"
                                className="book-lr-read-mode-exit"
                                onClick={() => setIsReadMode(false)}
                                aria-label="Exit read mode"
                                title="Exit read mode"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </div>
                    )}

                    {isLoading ? (
                        <section className="book-lr-state-card">
                            <div className="book-lr-loader"></div>
                            <strong>Preparing your lesson</strong>
                            <p>Loading the reading pages and saved progress.</p>
                        </section>
                    ) : pageError ? (
                        <section className="book-lr-state-card error">
                            <i className="bi bi-exclamation-triangle"></i>
                            <strong>Unable to open this lesson</strong>
                            <p>{pageError}</p>
                            <button type="button" onClick={loadReader}>Try again</button>
                        </section>
                    ) : !currentSlide ? (
                        <section className="book-lr-state-card">
                            <i className="bi bi-journal-text"></i>
                            <strong>No reading content yet</strong>
                            <p>This lesson does not have published content available.</p>
                        </section>
                    ) : (
                        <>
                            <section className="book-lr-deck-shell" aria-label="Lesson reading content">
                                <article
                                    className={`book-lr-slide ${isNoteMode ? "is-note-mode" : ""}`}
                                >
                                    {!isReadMode && (
                                        <div className="book-lr-slide-ribbon">
                                            Page {currentIndex + 1} of {slides.length}
                                        </div>
                                    )}

                                    {isReadMode && (
                                        <div className="book-lr-read-mode-meta">
                                            <span>{book?.title || "My Book"}</span>
                                            <strong>{lessonData?.title || "Lesson reader"}</strong>
                                            <small>Page {currentIndex + 1} of {slides.length}</small>
                                        </div>
                                    )}

                                    <div
                                        ref={htmlContentRef}
                                        className={`book-lr-html-content ${isNoteMode ? "is-note-mode" : ""}`}
                                    >
                                        <div
                                            ref={contentStageRef}
                                            className="book-lr-content-stage"
                                            onClick={createDraftNote}
                                        >
                                            <div
                                                ref={contentMarkupRef}
                                                className="book-lr-html-markup"
                                                dangerouslySetInnerHTML={{ __html: currentSlide.content || "" }}
                                            />

                                            {currentSlideNotes.map((note) => {
                                                const position = notePositions[getNotePositionKey(note)];
                                                if (!position) return null;

                                                return (
                                                    <button
                                                        key={note.id}
                                                        type="button"
                                                        className={`book-lr-note-chip ${activeNote?.id === note.id ? "is-active" : ""}`}
                                                        style={{
                                                            left: `${position.leftPx}px`,
                                                            top: `${position.topPx}px`,
                                                        }}
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            openExistingNote(note);
                                                        }}
                                                    >
                                                        <i className="bi bi-stickies-fill"></i>
                                                    </button>
                                                );
                                            })}

                                            {activeNote && currentSlide?.id === activeNote.slide && notePositions[getNotePositionKey(activeNote)] && (
                                                <div
                                                    className={`book-lr-note-cloud ${notePositions[getNotePositionKey(activeNote)].leftPercent > 72 ? "align-right" : ""} ${notePositions[getNotePositionKey(activeNote)].topPercent < 24 ? "align-bottom" : ""}`}
                                                    style={{
                                                        left: `${notePositions[getNotePositionKey(activeNote)].leftPx}px`,
                                                        top: `${notePositions[getNotePositionKey(activeNote)].topPx}px`,
                                                    }}
                                                    onClick={(event) => event.stopPropagation()}
                                                >
                                                    <div className="book-lr-note-cloud-head">
                                                        <strong>{activeNote.id ? "Edit Note" : "New Note"}</strong>
                                                        <button type="button" onClick={closeActiveNote}>
                                                            <i className="bi bi-x-lg"></i>
                                                        </button>
                                                    </div>
                                                    <textarea
                                                        value={activeNote.content || ""}
                                                        onChange={handleNoteContentChange}
                                                        placeholder="Write your note here..."
                                                        rows={4}
                                                    />
                                                    <div className="book-lr-note-cloud-actions">
                                                        <button
                                                            type="button"
                                                            className="book-lr-note-secondary"
                                                            onClick={activeNote.id ? deleteActiveNote : closeActiveNote}
                                                            disabled={isNoteSaving}
                                                        >
                                                            {activeNote.id ? "Delete" : "Cancel"}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="book-lr-note-primary"
                                                            onClick={saveActiveNote}
                                                            disabled={isNoteSaving}
                                                        >
                                                            {isNoteSaving ? "Saving..." : "Save Note"}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </article>
                            </section>

                            {!isReadMode ? (
                                <footer className="book-lr-bottom-nav">
                                    <button
                                        type="button"
                                        className="book-lr-nav-btn"
                                        disabled={currentIndex === 0 || isSaving}
                                        onClick={goPrevious}
                                    >
                                        <i className="bi bi-arrow-left"></i>
                                        Previous
                                    </button>

                                    <div className="book-lr-slide-strip" aria-label="Lesson page navigation">
                                        {slides.map((slide, index) => (
                                            <button
                                                key={slide.id || index}
                                                type="button"
                                                className={`book-lr-slide-dot ${index === currentIndex ? "active" : ""} ${getSlideStatus(slide) === "completed" ? "done" : ""} ${index > maxAccessibleIndex ? "locked" : ""}`}
                                                disabled={isSaving || index > maxAccessibleIndex}
                                                onClick={() => goToIndex(index)}
                                                title={index > maxAccessibleIndex ? "Complete previous pages to unlock this page." : slide.title || `Page ${index + 1}`}
                                            >
                                                <span>{index + 1}</span>
                                                <strong>
                                                    {index > maxAccessibleIndex
                                                        ? "Locked"
                                                        : getSlideStatus(slide) === "completed"
                                                            ? "Done"
                                                            : "Read"}
                                                </strong>
                                            </button>
                                        ))}
                                    </div>

                                    <button
                                        type="button"
                                        className="book-lr-complete-btn"
                                        disabled={isSaving || getSlideStatus(currentSlide) === "completed"}
                                        onClick={markCurrentComplete}
                                    >
                                        {getSlideStatus(currentSlide) === "completed" ? "Completed" : "Mark complete"}
                                    </button>

                                    <button
                                        type="button"
                                        className="book-lr-nav-btn primary"
                                        disabled={isSaving}
                                        onClick={goNext}
                                    >
                                        {currentIndex === slides.length - 1 ? "Finish" : "Next"}
                                        <i className="bi bi-arrow-right"></i>
                                    </button>
                                </footer>
                            ) : (
                                <footer className="book-lr-read-mode-footer">
                                    <button
                                        type="button"
                                        className="book-lr-read-next-btn"
                                        disabled={isSaving}
                                        onClick={goNext}
                                    >
                                        {isSaving
                                            ? "Saving..."
                                            : currentIndex === slides.length - 1
                                                ? "Finish Lesson"
                                                : "Next Page"}
                                        <i className="bi bi-arrow-right"></i>
                                    </button>
                                </footer>
                            )}
                        </>
                    )}
                </main>
            </div>
        </div>
    );
};

export default BookLessonReaderPage;
